// Shared by mobile and desktop Activity Help. The selected Activity version is
// authoritative for ordinary templates; Reading Deluxe may use a different
// presentation (Help) language from the language of its reading text.
const normalizeHelpLocale = (value) => {
  const code = typeof value === 'object' && value !== null
    ? (value.code || value.value || '') : value;
  return String(code || '').trim().toLowerCase().replace('_', '-').split('-')[0];
};

const getHelpVersionLocale = (versionType) => {
  const version = String(versionType || '').trim();
  const separator = version.indexOf('_');
  return separator < 0 ? '' : normalizeHelpLocale(version.slice(separator + 1));
};

const nonEmpty = (value) => value !== undefined && value !== null && value !== '';
const authoredAudio = (value) => typeof value === 'string'
  && !!value.trim() && value.trim().toUpperCase() !== 'INHERITED';

const resolveActivityHelpLocale = ({
  currentActivity, versionType, placementLocaleCode,
  activityLocale, solutionLocale, language, helpLocale,
} = {}) => {
  const versionLocale = getHelpVersionLocale(versionType);
  const placement = normalizeHelpLocale(placementLocaleCode);
  const activity = currentActivity?.activity || {};
  const monolingual = activity.monolingual && normalizeHelpLocale(activity.locale);
  const activityLanguage = normalizeHelpLocale(activityLocale);
  const uiLanguage = normalizeHelpLocale(solutionLocale);
  const explicitLanguage = normalizeHelpLocale(language);
  const configuredHelpLocale = normalizeHelpLocale(helpLocale || currentActivity?.data?.helpLocale);
  const configuredDefault = normalizeHelpLocale(currentActivity?.data?.multiLocaleHelp?.defaultLocale);
  const template = String(currentActivity?.template?.alias || '').toUpperCase().replace(/-/g, '_');

  // Reading Deluxe allows Spanish UI/Help with English reading material.
  // If a localized non-English version is actively selected, it still wins.
  if (template === 'READING_DELUXE') {
    return (versionLocale && versionLocale !== 'en' ? versionLocale : '')
      || placement || monolingual || activityLanguage || uiLanguage
      || versionLocale || explicitLanguage || configuredHelpLocale || configuredDefault || 'en';
  }

  return placement || monolingual || versionLocale || explicitLanguage
    || activityLanguage || configuredHelpLocale || uiLanguage || configuredDefault || 'en';
};

const findHelpString = (strings, stringNumber) => Array.isArray(strings)
  ? strings.find((item) => Number(item?.stringNumber) === Number(stringNumber))
  : null;

// Content and audio are resolved together from a single locale and Help String.
// No template-specific string IDs or hardcoded Activity IDs.
const resolveActivityHelpContent = ({
  data = {}, template = {}, currentActivity = {}, versionType,
  locale, strings, translate, getStringAudio,
  s3bucketPublicURL, mobileRuntime = false,
} = {}) => {
  const target = normalizeHelpLocale(locale) || 'en';
  const versionLocale = getHelpVersionLocale(versionType);
  const multi = data.multiLocaleHelp || {};
  const defaultLocale = normalizeHelpLocale(multi.defaultLocale);
  const current = multi[target] || {};
  const configuredDefault = multi[defaultLocale] || {};
  const templateWordlist = template?.helpStrings?.wordlist;
  const candidates = [
    current.helpString,
    configuredDefault.helpString,
    data.helpString,
    multi.en?.helpString,
    ...Object.keys(multi).filter((key) => key !== 'defaultLocale').map((key) => multi[key]?.helpString),
    data.isGenerated ? templateWordlist : null,
  ];
  const helpString = candidates.find(nonEmpty) || '';
  const stringRecord = nonEmpty(helpString) ? findHelpString(strings, helpString) : null;
  const localizedString = stringRecord?.data?.[target] || {};
  const hasLocalizedStringText = [localizedString.prettyValue, localizedString.stringValue]
    .some((value) => typeof value === 'string' && !!value.trim());

  const translatedText = hasLocalizedStringText && translate
    ? translate(helpString, '', false, true)
    : '';
  const hasLocalizedHelpHtml = typeof current.helpHtml === 'string' && !!current.helpHtml.trim();
  const baseLanguage = normalizeHelpLocale(currentActivity?.activity?.locale);
  const isNativeBase = (currentActivity?.activity?.monolingual && (!baseLanguage || baseLanguage === target))
    || baseLanguage === target || defaultLocale === target;
  const baseIsEnglish = target === 'en' && !versionType;
  const isLegacyVmMobileBase = mobileRuntime && target === 'vm' && versionLocale === 'vm';
  const baseText = data.helpHtml || '';
  const helpHtml = (typeof translatedText === 'string' && translatedText !== 'Translate not found' && !!translatedText.trim()
    ? translatedText : '')
    || (hasLocalizedHelpHtml ? current.helpHtml : '')
    || (isNativeBase || baseIsEnglish ? baseText : '')
    || baseText
    || configuredDefault.helpHtml || multi.en?.helpHtml || '';
  const textSource = translatedText && translatedText !== 'Translate not found' ? 'localized-string'
    : hasLocalizedHelpHtml ? 'localized-html'
      : (helpHtml ? 'base-fallback' : 'missing');

  let audioHelpUrl = null;
  let audioSource = 'missing';
  let helpAudio = '';
  const stringAudio = localizedString.stringAudio || localizedString.audio;
  if (authoredAudio(stringAudio) && typeof getStringAudio === 'function') {
    const { audio, path } = getStringAudio(helpString, true);
    if (audio && path && audio === stringAudio) {
      helpAudio = audio;
      audioHelpUrl = `${path}${audio}`;
      audioSource = 'localized-string';
    }
  }

  // Authoring HelpTab uploads directly to gamedata when not using a numbered
  // String; languageVersionData.helpAudio explicitly lives in its own folder.
  if (!audioHelpUrl) {
    let folder = '';
    if (authoredAudio(current.helpAudio)) {
      helpAudio = current.helpAudio;
      folder = 'gamedata';
      audioSource = 'localized-authored';
    } else {
      const versionAudio = versionType && versionLocale === target
        ? data.languageVersionData?.[versionType]?.helpAudio : null;
      if (authoredAudio(versionAudio)) {
        helpAudio = versionAudio;
        folder = `languageVersionData/${versionType}`;
        audioSource = 'localized-version';
      } else if ((isNativeBase || baseIsEnglish || isLegacyVmMobileBase) && authoredAudio(data.helpAudio)) {
        helpAudio = data.helpAudio;
        folder = 'gamedata';
        audioSource = isLegacyVmMobileBase && !isNativeBase
          ? 'legacy-vm-base-unverified' : 'native-base';
      }
    }
    if (folder && currentActivity?.activity?.contentFolder && s3bucketPublicURL) {
      audioHelpUrl = `${s3bucketPublicURL}/${currentActivity.activity.contentFolder}/${folder}/${helpAudio}`;
    }
  }
  return {
    helpString, helpHtml, helpAudio, audioHelpUrl,
    textSource, audioSource, locale: target,
    hasLocalizedStringText, hasLocalizedHelpHtml,
    hasLocalizedHelpAudio: authoredAudio(current.helpAudio),
    templateWordlistHelpString: templateWordlist || '',
    shouldUseTemplateWordlistHelpString: !!data.isGenerated && !!templateWordlist
      && helpString === templateWordlist,
  };
};

export { normalizeHelpLocale, getHelpVersionLocale, resolveActivityHelpLocale, resolveActivityHelpContent };
